import { registerAuthTokenProvider } from '@realtyfeed/data-access';
import { signOutUser } from './services/auth.service';
import { fetchStaffPermissions } from './services/permission.service';
import {
  getAccessToken,
  getIdToken,
  getIdTokenClaims,
  getSessionTokens,
} from './services/token.service';
import { useAuthSessionStore } from './stores/auth-session-store';

/**
 * Wires auth into the API client and resolves the initial session.
 *
 * Each app calls this once in `main.tsx`, after `configureAuth()` and before
 * rendering. Two things happen here and nowhere else:
 *
 * 1. The token provider is handed to `@realtyfeed/data-access`. That library
 *    cannot import this one - it is tagged `type:data-access` and may only depend
 *    on the `type:util` layer, and the dependency would be a genuine cycle since
 *    auth's own calls go through the API client. Registration inverts it.
 * 2. The session store is filled, so the route guards have an answer other than
 *    `'unknown'` on the first render.
 *
 * `onSessionExpired` signs out rather than only clearing local state: leaving
 * Amplify's session in place would let the next `fetchAuthSession` silently
 * resurrect a session the backend has already rejected.
 */
export const bootstrapAuth = async (): Promise<void> => {
  registerAuthTokenProvider({
    getAccessToken,
    getIdToken,
    onSessionExpired: () => {
      const { status, clearSession } = useAuthSessionStore.getState();
      // Guard against a burst of failing requests each triggering a sign-out.
      if (status === 'unauthenticated') return;
      clearSession();
      void signOutUser();
    },
  });

  const tokens = await getSessionTokens();
  if (!tokens) {
    useAuthSessionStore.getState().setUnauthenticated();
    return;
  }

  useAuthSessionStore.getState().setSession(tokens, await getIdTokenClaims());
  await loadPermissions();
};

/**
 * Fetches the permission grant into the session store.
 *
 * Runs after the session is set, never before: the request is authorised with the session's
 * own token, so calling it first would send an unauthenticated request and cache the empty
 * grant that comes back.
 */
export const loadPermissions = async (): Promise<void> => {
  useAuthSessionStore.getState().setPermissions(await fetchStaffPermissions());
};

/**
 * Re-reads the session after a sign-in or a claims-changing action.
 *
 * The permission grant is refetched too. A role edited in the Roles screen changes what the
 * editing session may see, and without this the sidebar keeps the grant it started with until
 * a hard reload — which is exactly the stale-permissions behaviour the legacy app had.
 */
export const refreshSession = async (): Promise<void> => {
  const tokens = await getSessionTokens({ forceRefresh: true });
  if (!tokens) {
    useAuthSessionStore.getState().setUnauthenticated();
    return;
  }
  useAuthSessionStore.getState().setSession(tokens, await getIdTokenClaims());
  await loadPermissions();
};
